<template>
  <Modal ref="modalRef" width="40rem">
    <template #body>
      <View gap="xl" direction="column">
        <View gap="md" direction="column" align="center">
          <Text variant="h2">Pause listing?</Text>
          <Text variant="body-large" color="secondary" align="center">
            We will temporarily pause your listing while maintaining your current renter pipeline
          </Text>
        </View>

        <Hint gap="md">
          <template #icon>
            <IconTile icon="info-circle" size="small" color="yellow" />
          </template>

          <View gap="sm" direction="column">
            <Text variant="h5">Important notice</Text>
            <Text variant="body-medium">
              Pausing your listing will remove it from all listing platforms, but your current renter pipeline will
              remain active. You can resume your listing at any time.
            </Text>
          </View>
        </Hint>

        <WhatToExpectCard :items="whatToExpectNextItems" />
      </View>
    </template>

    <template #footer>
      <View gap="md" :grow="1" class="actions">
        <Button variant="secondary" @click="close">Cancel</Button>
        <Button variant="primary" :loading="isLoading" @click="submit">Pause listing</Button>
      </View>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { useQueryCache } from '@pinia/colada';
import { ref } from 'vue';

import { Button, Hint, IconTile, Modal, Text, View, WhatToExpectCard, WhatToExpectItem } from '@tallo/design-system';
import { getPropertyInquiriesQueryKey, usePropertyStore } from '@tallo/investor/property';
import { showingsQueryKey } from '@tallo/investor/showing';

const queryCache = useQueryCache();
const propertyStore = usePropertyStore();
const modalRef = ref<InstanceType<typeof Modal>>();
const isLoading = ref(false);

const whatToExpectNextItems: WhatToExpectItem[] = [
  {
    icon: 'x-close-circle',
    description: 'Property will be removed from the listing platforms',
  },
  {
    icon: 'message-chat-circle',
    description: 'New inquiries will be informed that the unit is no longer available',
  },
  {
    icon: 'leads',
    description: 'Existing conversations and scheduled showings will continue normally',
  },
];

async function submit() {
  isLoading.value = true;

  const propertyId = propertyStore.property?.id;

  propertyStore
    .pausePropertyListing(propertyId!)
    .then(async () => {
      await Promise.all([
        queryCache.invalidateQueries({ key: [showingsQueryKey] }),
        queryCache.invalidateQueries({ key: getPropertyInquiriesQueryKey(propertyId!) }),
      ]);
      close();
    })
    .catch((error) => {
      console.error('Error pausing property listing:', error);
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function close() {
  modalRef.value?.close();
}

defineExpose({
  modalRef,
});
</script>

<style scoped lang="scss">
.actions > * {
  width: 100%;
}
</style>
